import type { ExpandingThumb } from '../HeroSlider.types';

/**
 * CSS-synced timing constants — MUST match HeroSlider.module.scss transitions.
 *
 * When updating CSS transition durations, update these constants to match:
 *   .expandingOverlay / .shrinkingOverlay → THUMB_MORPH_MS
 *   .dynamicContent (exit)               → CONTENT_FADE_OUT_MS
 *   .dynamicSlide.active (enter)         → SLIDE_CROSSFADE_MS
 *   .thumbnailEnterGrow                  → THUMB_ENTER_GROW_MS
 */
export const ANIMATION_TIMING = {
  THUMB_MORPH_MS: 500,
  CONTENT_FADE_OUT_MS: 300,
  SLIDE_CROSSFADE_MS: 1000,
  THUMB_ENTER_GROW_MS: 500,
} as const;

export const EXPAND_SWITCH_DELAY = ANIMATION_TIMING.THUMB_MORPH_MS;
export const EXPAND_CLEANUP_DELAY =
  ANIMATION_TIMING.THUMB_MORPH_MS + ANIMATION_TIMING.SLIDE_CROSSFADE_MS + 100;
export const SHRINK_COLLAPSE_DELAY = ANIMATION_TIMING.CONTENT_FADE_OUT_MS;
export const SHRINK_REVEAL_DELAY =
  ANIMATION_TIMING.CONTENT_FADE_OUT_MS + ANIMATION_TIMING.THUMB_MORPH_MS + 50;
export const SHRINK_CLEANUP_DELAY = SHRINK_REVEAL_DELAY + ANIMATION_TIMING.THUMB_ENTER_GROW_MS + 50;

// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------

export type AnimationPhase =
  | 'idle'
  | 'expanding'
  | 'expand-switching'
  | 'shrink-fading'
  | 'shrink-collapsing'
  | 'shrink-revealing';

export interface AnimationState {
  phase: AnimationPhase;
  animationId: number;
  expandingThumb: ExpandingThumb | null;
  isExpanded: boolean;
  contentRaised: boolean;
  hideGradient: boolean;
  shrinkingThumb: ExpandingThumb | null;
  isShrunk: boolean;
  enteringIndex: number | null;
  enteringVisible: boolean;
  fadingOutIndex: number | null;
}

export type AnimationAction =
  | { type: 'EXPAND_START'; thumb: ExpandingThumb }
  | { type: 'EXPAND_GROW' }
  | { type: 'EXPAND_SWITCH' }
  | { type: 'EXPAND_CLEANUP' }
  | { type: 'SHRINK_START'; thumb: ExpandingThumb; fadingOutIndex: number }
  | { type: 'SHRINK_COLLAPSE'; enteringIndex: number }
  | { type: 'SHRINK_GROW' }
  | { type: 'SHRINK_REVEAL' }
  | { type: 'SHRINK_CLEANUP' };

export const INITIAL_ANIMATION_STATE: AnimationState = {
  phase: 'idle',
  animationId: 0,
  expandingThumb: null,
  isExpanded: false,
  contentRaised: false,
  hideGradient: false,
  shrinkingThumb: null,
  isShrunk: false,
  enteringIndex: null,
  enteringVisible: false,
  fadingOutIndex: null,
};

export const animationReducer = (
  state: AnimationState,
  action: AnimationAction
): AnimationState => {
  switch (action.type) {
    case 'EXPAND_START':
      return {
        ...INITIAL_ANIMATION_STATE,
        animationId: state.animationId + 1,
        phase: 'expanding',
        expandingThumb: action.thumb,
      };

    case 'EXPAND_GROW':
      if (state.phase !== 'expanding') return state;
      return { ...state, isExpanded: true };

    case 'EXPAND_SWITCH':
      if (state.phase !== 'expanding') return state;
      return {
        ...state,
        phase: 'expand-switching',
        contentRaised: true,
        hideGradient: true,
      };

    case 'EXPAND_CLEANUP':
      if (state.phase !== 'expand-switching') return state;
      return { ...INITIAL_ANIMATION_STATE };

    case 'SHRINK_START':
      return {
        ...INITIAL_ANIMATION_STATE,
        animationId: state.animationId + 1,
        phase: 'shrink-fading',
        shrinkingThumb: action.thumb,
        fadingOutIndex: action.fadingOutIndex,
      };

    case 'SHRINK_COLLAPSE':
      if (state.phase !== 'shrink-fading') return state;
      return {
        ...state,
        phase: 'shrink-collapsing',
        fadingOutIndex: null,
        enteringIndex: action.enteringIndex,
      };

    case 'SHRINK_GROW':
      if (state.phase !== 'shrink-collapsing') return state;
      return { ...state, isShrunk: true };

    case 'SHRINK_REVEAL':
      if (state.phase !== 'shrink-collapsing') return state;
      return {
        ...state,
        phase: 'shrink-revealing',
        enteringVisible: true,
      };

    case 'SHRINK_CLEANUP':
      if (state.phase !== 'shrink-revealing') return state;
      return { ...INITIAL_ANIMATION_STATE };

    default:
      return state;
  }
};

// ---------------------------------------------------------------------------
// Derived phase helpers
// ---------------------------------------------------------------------------

export const isExpandPhase = (phase: AnimationPhase): boolean =>
  phase === 'expanding' || phase === 'expand-switching';

export const isShrinkPhase = (phase: AnimationPhase): boolean =>
  phase === 'shrink-fading' || phase === 'shrink-collapsing';

export const isAnimating = (phase: AnimationPhase): boolean => phase !== 'idle';
